// web/src/api/ticket/templates.ts —— 工单创建模板（v1.7 快捷建单）与采集字段解析
// 拆自 web/src/api/ticket.ts 的同名分区（内容原样搬运）；由 web/src/api/ticket.ts 以 export * 聚合，导出面对调用方不变。

import { del, get, post, put } from '../../request';
import { toPage, type Page, type PageQuery } from '../types';

import type { Ticket, TicketForm } from './enhance';

// ---------- 工单创建模板（v1.7 快捷建单） ----------

/** 工单创建模板：预填基础字段 + 动态字段默认值（field_defaults 为 JSON 串） */
export interface TicketTemplate {
  id: number;
  name: string;
  type?: string;
  priority?: string;
  category?: string;
  title?: string;
  description?: string;
  workflow_template_id?: number;
  follow_up?: boolean;
  /** 动态字段默认值 JSON：{"field_key":"默认值"} */
  field_defaults?: string;
  /** v5 建单采集字段 JSON：[{field_key,label,type,required,question,source,crm_key}] */
  collect_fields?: string;
  /** v5 AI 建单命中播报动态提示语（空=不播） */
  announcement?: string;
  /** v5 信息核实开关（true 时坐席须确认/补全字段后才允许提交草稿） */
  verify_info?: boolean;
  remark?: string;
  enabled?: boolean;
  created_by?: number;
  created_at?: string;
  updated_at?: string;
}

export function listTicketTemplates(): Promise<TicketTemplate[]> {
  return get('/ticket-templates');
}

/** v5 建单采集字段条目（collect_fields JSON 数组元素） */
export interface TicketTemplateCollectField {
  field_key: string;
  label?: string;
  type?: string;
  required?: boolean;
}

/** 解析模板 collect_fields JSON → 采集字段数组（非法/空返回空） */
export function parseTicketTemplateCollect(raw?: string): TicketTemplateCollectField[] {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? (arr as TicketTemplateCollectField[]) : [];
  } catch {
    return [];
  }
}

/** 解析模板 field_defaults JSON → 键值 map（非法/空返回空） */
export function parseTicketTemplateDefaults(raw?: string): Record<string, string> {
  if (!raw) return {};
  try {
    const m = JSON.parse(raw);
    return m && typeof m === 'object' ? (m as Record<string, string>) : {};
  } catch {
    return {};
  }
}

/**
 * 模板所需动态字段 key（有序、去重）：collect_fields[].field_key ∪ field_defaults 的 key。
 * 建单表单按模板限定渲染这些字段（而非全量启用字段）。
 */
export function ticketTemplateFieldKeys(tpl?: TicketTemplate | null): string[] {
  if (!tpl) return [];
  const keys: string[] = [];
  const seen = new Set<string>();
  parseTicketTemplateCollect(tpl.collect_fields).forEach((f) => {
    if (f.field_key && !seen.has(f.field_key)) {
      seen.add(f.field_key);
      keys.push(f.field_key);
    }
  });
  Object.keys(parseTicketTemplateDefaults(tpl.field_defaults)).forEach((k) => {
    if (!seen.has(k)) {
      seen.add(k);
      keys.push(k);
    }
  });
  return keys;
}

/** 坐席建单可选（仅启用） */
export function listEnabledTicketTemplates(): Promise<TicketTemplate[]> {
  return get('/ticket-templates/active');
}

export function createTicketTemplate(
  p: Partial<Pick<TicketTemplate, 'name' | 'type' | 'priority' | 'category' | 'title' | 'description' | 'workflow_template_id' | 'follow_up' | 'field_defaults' | 'remark' | 'enabled'>>
): Promise<{ id: number }> {
  return post('/ticket-templates', p);
}

export function updateTicketTemplate(
  id: number,
  p: Partial<Pick<TicketTemplate, 'name' | 'type' | 'priority' | 'category' | 'title' | 'description' | 'workflow_template_id' | 'follow_up' | 'field_defaults' | 'remark' | 'enabled'>>
): Promise<{ id: number }> {
  return put(`/ticket-templates/${id}`, p);
}

export function deleteTicketTemplate(id: number): Promise<{ id: number }> {
  return del(`/ticket-templates/${id}`);
}

/** v1.16 多维派单规则 */
export interface TicketRouteRule {
  id?: number;
  name: string;
  category?: string;
  /** 多字段匹配条件：{"product_model":"X1","customer_level":"VIP"} */
  conditions?: string;
  responsible_skill_group_id: number;
  default_assignee_id?: number | null;
  workflow_template_id?: number;
  priority?: number;
  enabled?: boolean;
  created_at?: string;
}

export function listTicketRouteRules(): Promise<TicketRouteRule[]> {
  return get('/ticket-route-rules');
}
export function createTicketRouteRule(f: Partial<TicketRouteRule> & { conditions?: Record<string, string> }) {
  return post('/ticket-route-rules', f);
}
export function updateTicketRouteRule(id: number, f: Partial<TicketRouteRule> & { conditions?: Record<string, string> }) {
  return put(`/ticket-route-rules/${id}`, f);
}
export function deleteTicketRouteRule(id: number) {
  return del(`/ticket-route-rules/${id}`);
}

/** v1.16 分类树节点（大类→中类→小类级联） */
export interface VocabTreeNode {
  id: number;
  code: string;
  name: string;
  children?: VocabTreeNode[];
}

/** GET /intent/vocab-tree：工单级联分类树 */
export function getVocabTree(): Promise<VocabTreeNode[]> {
  return get('/intent/vocab-tree');
}

/** SLA 超时判定（看板/详情共用）：显式 overdue 优先；否则按截止时间（终态不计超时） */
export function isTicketOverdue(t: Ticket): boolean {
  if (typeof t.overdue === 'boolean') return t.overdue;
  if (!t.sla_due_at) return false;
  if (t.status === 'resolved' || t.status === 'closed' || t.status === 'completed') return false;
  return new Date(t.sla_due_at).getTime() < Date.now();
}

export interface TicketQuery extends PageQuery {
  status?: string;
  priority?: string;
  /** 客户手机号模糊查询 */
  phone?: string;
  /** 传 'true' 仅看 SLA 超时工单 */
  overdue?: string;
  /** 传 'true' 仅看无人负责工单（无处理人且无在途流程） */
  unowned?: string;
}

/** GET /tickets */
export async function listTickets(q: TicketQuery): Promise<Page<Ticket>> {
  return toPage<Ticket>(await get('/tickets', q));
}

/** GET /tickets/:id（详情内嵌 comments） */
export function getTicket(id: number): Promise<Ticket> {
  return get(`/tickets/${id}`);
}

/** 提交：camel → snake（customerId→customer_id、assigneeId→assignee_id、cdrId→cdr_id） */
function toPayload(f: Partial<TicketForm> & { status?: string }): Record<string, any> {
  const p: Record<string, any> = {};
  if (f.title !== undefined) p.title = f.title;
  if (f.type !== undefined) p.type = f.type;
  if (f.category !== undefined) p.category = f.category;
  if (f.description !== undefined) p.description = f.description;
  if (f.priority !== undefined) p.priority = f.priority;
  if (f.customerId !== undefined) p.customer_id = f.customerId;
  if (f.assigneeId !== undefined) p.assignee_id = f.assigneeId;
  if (f.cdrId !== undefined) p.cdr_id = f.cdrId;
  if (f.chatSessionId !== undefined) p.chat_session_id = f.chatSessionId;
  if (f.uuid !== undefined) p.uuid = f.uuid;
  if (f.customerPhone !== undefined) p.customer_phone = f.customerPhone;
  if (f.followUp !== undefined) p.follow_up = f.followUp;
  if (f.workflowTemplateId !== undefined) p.workflow_template_id = f.workflowTemplateId;
  if (f.content !== undefined) p.content = f.content;
  if (f.status !== undefined) p.status = f.status;
  // v1.16 动态字段（字段自定义）：仅当有值时透传
  if (f.dynamicFields !== undefined) p.dynamic_fields = f.dynamicFields;
  return p;
}

export function createTicket(f: TicketForm) {
  return post('/tickets', toPayload(f));
}

export function updateTicket(id: number, f: Partial<TicketForm> & { status?: string }) {
  return put(`/tickets/${id}`, toPayload(f));
}

/** DELETE /tickets/:id（草稿丢弃用：误判或不再需要的草稿；删除后不可恢复） */
export function deleteTicket(id: number) {
  return del(`/tickets/${id}`);
}

/** POST /tickets/:id/comments */
export function addTicketComment(id: number, content: string) {
  return post(`/tickets/${id}/comments`, { content });
}

/** POST /tickets/:id/assign —— 转派 */
export function submitTicketWorkflow(id: number) {
  return post(`/tickets/${id}/submit-workflow`, {});
}

/** POST /tickets/:id/switch-workflow —— v1.11 多流程转接：取消在途流程，用新模板重新发起审批 */
export function switchTicketWorkflow(id: number, workflowTemplateId: number) {
  return post(`/tickets/${id}/switch-workflow`, { workflow_template_id: workflowTemplateId });
}

export function assignTicket(id: number, assigneeId: number) {
  return post(`/tickets/${id}/assign`, { assignee_id: assigneeId });
}

